import { inject, Injectable } from '@angular/core';
import { WebProviderService } from '../web/web.provider.service';
import { getAssetURL } from 'src/app/constants/assets';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ThumbnailProviderService {
  private webProvider = inject(WebProviderService);
  private readonly baseURL = environment.apiURL;
  private get attributeURL() {
    return '/api/Thumbnails';
  }

  /**
   * The thumbnail submission currently in flight, if any.
   *
   * Add-to-cart waits on this before telling anyone the ring is in the cart, so a retailer never
   * receives a cart event for a ring whose image has not been stored yet (ADO #8902). The upload
   * is a synchronous write all the way to blob storage, so once this settles the image really is
   * readable at `retailer-webimages/{iframeID}/{style}_P1.jpg`.
   *
   * It deliberately never rejects: a thumbnail that fails to upload must not block the sale.
   * Callers get "the submission has finished", not "the image exists".
   */
  private submission: Promise<void> = Promise.resolve();

  /**
   * How long anything will wait for a thumbnail before giving up and carrying on without it.
   *
   * A failed upload must not block the sale, and a *hung* one is just a failure that has not
   * finished failing yet -- without this, a stalled request would hang add-to-cart behind a
   * spinner indefinitely. The clock runs from the start of the submission, not from each waiter,
   * so a customer never waits more than this once in total.
   */
  private static readonly SUBMISSION_TIMEOUT_MS = 10000;

  /**
   * Runs `work` as the tracked thumbnail submission, replacing any previous one. Replacing rather
   * than chaining matters because re-entering the review page starts a fresh submission, and
   * add-to-cart should wait on that one, not on a stale earlier attempt.
   *
   * Returns the same bounded promise that {@link whenSubmissionSettled} exposes, so every waiter
   * shares one deadline.
   */
  trackSubmission(work: () => Promise<void>): Promise<void> {
    const running = work().catch(() => undefined);

    let timer: ReturnType<typeof setTimeout> | undefined;
    const bounded = Promise.race([
      running,
      new Promise<void>((resolve) => {
        timer = setTimeout(() => {
          console.warn(`Thumbnail submission did not settle within ${ThumbnailProviderService.SUBMISSION_TIMEOUT_MS}ms; continuing without it.`);
          resolve();
        }, ThumbnailProviderService.SUBMISSION_TIMEOUT_MS);
      }),
    ]).finally(() => clearTimeout(timer));

    this.submission = bounded;
    return bounded;
  }

  /**
   * Resolves once the in-flight thumbnail submission has settled -- successfully, unsuccessfully,
   * or by hitting {@link SUBMISSION_TIMEOUT_MS}. Never rejects.
   */
  whenSubmissionSettled(): Promise<void> {
    return this.submission;
  }

  async doesThumbnailExist(style: string) {
    try {
      const data = await this.webProvider.get<string>(getAssetURL(`sm/${style}_P1.jpg`, 'webimages'));
      return !!data;
    } catch(e) {
      return false;
    }
  }

  async submitThumbnail(styleNumber: string, imageData: string, retailerRingBuilderID: string, customerName: string, p: string) {
    try {
      await this.webProvider.post(`${this.baseURL}${this.attributeURL}/${styleNumber}?ringBuilderID=${retailerRingBuilderID}&customerName=${customerName}&p=${p}`, { imageData });
    } catch(e) {
      console.error(e);
    }
  }

  async submitRetailerThumbnail(styleNumber: string, imageData: string, retailerIframeID: string) {
    try {
      await this.webProvider.post(`${this.baseURL}${this.attributeURL}/retailer/${retailerIframeID}/${styleNumber}`, { imageData });
    } catch(e) {
      console.error(e);
    }
  }

  /** @deprecated Use {@link submitThumbnail} with a `retailerRingBuilderID` instead. */
  async submitThumbnailLegacy(styleNumber: string, imageData: string, customerID: string, customerName: string, p: string) {
    try {
      await this.webProvider.post(`${this.baseURL}${this.attributeURL}/${styleNumber}/Legacy?customerNumber=${customerID}&customerName=${customerName}&p=${p}`, { imageData });
    } catch(e) {
      console.error(e);
    }
  }
}
